import { z } from 'zod'

// Catégories de classement des demandes (Demandes-V3, mandat du 30 oct. 2026). Une catégorie organise la boîte de
// travail; elle ne remplace jamais le statut de suivi. Les couleurs sont des tons du Design System, jamais libres.

export const leadCategoryTones = ['info', 'warning', 'neutral', 'pending', 'success', 'danger'] as const
export type LeadCategoryTone = (typeof leadCategoryTones)[number]

export const leadCategoryToneLabel: Record<LeadCategoryTone, string> = {
  info: 'Bleu',
  warning: 'Ambre',
  neutral: 'Gris',
  pending: 'Violet',
  success: 'Vert',
  danger: 'Rose',
}

/** Couleur de la pastille : jeton du thème courant (clair ou sombre). */
export const leadCategoryToneColor: Record<LeadCategoryTone, string> = {
  info: 'var(--status-info-foreground)',
  warning: 'var(--status-warning-foreground)',
  neutral: 'var(--status-neutral-foreground)',
  pending: 'var(--status-pending-foreground)',
  success: 'var(--status-success-foreground)',
  danger: 'var(--status-danger-light-foreground)',
}

export const leadCategoryNameMax = 40

export const leadCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  tone: z.enum(leadCategoryTones),
  sortOrder: z.number().int(),
  archivedAt: z.string().nullable(),
  leadCount: z.number().int(),
})
export type LeadCategory = z.infer<typeof leadCategorySchema>

export const leadCategoryAssignmentSchema = z.object({
  leadId: z.string(),
  categoryId: z.string(),
  assignedAt: z.string(),
})
export type LeadCategoryAssignment = z.infer<typeof leadCategoryAssignmentSchema>

export const leadPositionSchema = z.object({
  leadId: z.string(),
  latitude: z.number(),
  longitude: z.number(),
})
export type LeadPosition = z.infer<typeof leadPositionSchema>

/** Lecture groupée de la boîte de tri : une requête pour toute la liste, jamais une par demande. */
export const demandesTriageSchema = z.object({
  categories: z.array(leadCategorySchema),
  assignments: z.array(leadCategoryAssignmentSchema),
  positions: z.array(leadPositionSchema),
})
export type DemandesTriage = z.infer<typeof demandesTriageSchema>

export interface CreateLeadCategoryCommand {
  name: string
  tone: LeadCategoryTone
}

export interface UpdateLeadCategoryCommand extends CreateLeadCategoryCommand {
  categoryId: string
}

export interface AssignLeadCategoryCommand {
  leadId: string
  /** `null` : retirer la demande de sa catégorie. */
  categoryId: string | null
}

export function isArchived(category: Pick<LeadCategory, 'archivedAt'>): boolean {
  return category.archivedAt !== null
}

/** Catégories actives dans l'ordre du menu Déplacer. */
export function activeCategories(categories: readonly LeadCategory[]): LeadCategory[] {
  return categories.filter((category) => !isArchived(category)).sort((a, b) => a.sortOrder - b.sortOrder)
}

export function archivedCategories(categories: readonly LeadCategory[]): LeadCategory[] {
  return categories.filter(isArchived)
}

/** Le nom accompagne toujours la couleur; une catégorie désactivée le dit. */
export function categoryDisplayName(category: Pick<LeadCategory, 'name' | 'archivedAt'>): string {
  return isArchived(category) ? `${category.name} (désactivée)` : category.name
}

/** Premier ton encore libre parmi les catégories actives, sinon le premier ton. */
export function nextFreeTone(categories: readonly LeadCategory[]): LeadCategoryTone {
  const used = new Set(activeCategories(categories).map((category) => category.tone))
  return leadCategoryTones.find((tone) => !used.has(tone)) ?? leadCategoryTones[0]
}

/** Même règle que le serveur : 1 à 40 caractères, nom actif unique sans égard à la casse. */
export function leadCategoryNameError(
  name: string,
  categories: readonly LeadCategory[],
  exceptId?: string,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Le nom est obligatoire.'
  if (trimmed.length > leadCategoryNameMax) {
    return `Le nom compte au plus ${String(leadCategoryNameMax)} caractères.`
  }
  const taken = activeCategories(categories).some(
    (category) => category.id !== exceptId && category.name.trim().toLowerCase() === trimmed.toLowerCase(),
  )
  return taken ? 'Une catégorie active porte déjà ce nom.' : null
}

/** Déplace une catégorie d'un cran dans l'ordre actif; renvoie la liste complète des identifiants actifs. */
export function movedCategoryOrder(
  categories: readonly LeadCategory[],
  categoryId: string,
  offset: -1 | 1,
): string[] | null {
  const ids = activeCategories(categories).map((category) => category.id)
  const from = ids.indexOf(categoryId)
  const to = from + offset
  if (from < 0 || to < 0 || to >= ids.length) return null
  const next = [...ids]
  const [moved] = next.splice(from, 1)
  if (moved === undefined) return null
  next.splice(to, 0, moved)
  return next
}

export function leadCategoryErrorMessage(code: string): string {
  switch (code) {
    case 'LEAD_CATEGORY_NAME_TAKEN':
      return 'Une catégorie active porte déjà ce nom.'
    case 'LEAD_CATEGORY_NAME_INVALID':
      return `Le nom compte de 1 à ${String(leadCategoryNameMax)} caractères.`
    case 'LEAD_CATEGORY_FORBIDDEN':
    case 'LEAD_UPDATE_FORBIDDEN':
      return 'Vous n’avez pas la permission de faire cette modification.'
    case 'LEAD_CATEGORY_NOT_FOUND':
      return 'Cette catégorie n’existe plus ou est désactivée. Rechargez la page.'
    case 'LEAD_CATEGORY_ORDER_STALE':
      return 'Les catégories ont changé entre-temps. Rechargez la page.'
    case 'LEAD_NOT_FOUND':
      return 'Cette demande n’existe plus.'
    default:
      return 'La modification n’a pas pu être enregistrée.'
  }
}
